import { insightsDesignEnabled } from "../../common/utils/insightsDesignEnabled";
import { ink, paper, stone } from "./design/designTokens";

export const DEFAULT_AGGREGATE_PAGE_SIZE = 25;
export const DEFAULT_AGGREGATE_GRID_PAGE_SIZE = 24;
/** Page-size choices for the aggregate table (print uses the current page). */
export const AGGREGATE_TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
/** Page-size choices for the aggregate card grid. Prefer multiples of 4; 100 is included for print. */
export const AGGREGATE_GRID_PAGE_SIZE_OPTIONS = [8, 16, 24, 48, 72, 100];

/** sessionStorage key for aggregate results table vs card view (`'table'` | `'card'`). */
export const PRICING_REPORT_RESULTS_VIEW_SESSION_KEY = "pricingReportResultsView";

/** Default Include Providers selection for pricing reports (Beef AI `price_history.provider` values). */
export const DEFAULT_PRICING_REPORT_PROVIDERS_INCLUDE = [
  "workflow:website-scans",
  "workflow:street-team",
];

/** Default weight unit for pricing report aggregates and charts. */
export const DEFAULT_PRICING_REPORT_UNIT = "kg";

export const discountFilterChoices = [
  { value: false, label: "Regular Pricing" },
  { value: true, label: "Promotional Pricing" },
];

export const cadCurrencyFormat = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
  maximumFractionDigits: 2,
});

/** Table/export display for aggregate price cells. */
export function formatAggregateCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  return cadCurrencyFormat.format(Number(value));
}

/** CSV export display for aggregate net weight (normalized kg). */
export function formatAggregateNetWeightKg(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Number(value).toFixed(3)} kg`;
}

/** CSV export display for total/package weight from source quantity (raw_unit_quantity). */
export function formatAggregateTotalWeight(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toFixed(3);
}

export const PRICING_CSV_COLUMNS = [
  { key: "month", label: "Month" },
  { key: "productName", label: "Product" },
  { key: "province", label: "Province" },
  { key: "retailer", label: "Retailer" },
  { key: "provider", label: "Provider" },
  { key: "avgUnit", label: "Avg (per kg)" },
  { key: "lowUnit", label: "Low" },
  { key: "highUnit", label: "High" },
  { key: "avgTotalWeight", label: "Avg total weight" },
  { key: "lowTotalWeight", label: "Low total weight" },
  { key: "highTotalWeight", label: "High total weight" },
  { key: "avgNetWeight", label: "Avg net weight (kg)" },
  { key: "lowNetWeight", label: "Low net weight (kg)" },
  { key: "highNetWeight", label: "High net weight (kg)" },
  { key: "n", label: "Count" },
];

/** One row per `price_history` record for the active filters (no aggregates). */
export const PRICING_RAW_CSV_COLUMNS = [
  { key: "recordDate", label: "Date" },
  { key: "productName", label: "Product" },
  { key: "province", label: "Province" },
  { key: "retailer", label: "Retailer" },
  { key: "provider", label: "Provider" },
  { key: "unitPrice", label: "Unit price (per kg)" },
  { key: "totalWeight", label: "Total weight" },
  { key: "netWeight", label: "Net weight (kg)" },
];

export const AGGREGATE_TABLE_EMPHASIZED_COLUMN_KEYS = new Set([
  'month',
  "avgUnit",
  "n",
]);

export const AGGREGATE_TABLE_COLUMNS = [
  { key: "month", label: "Month" },
  { key: "productName", label: "Product" },
  { key: "province", label: "Province" },
  { key: "retailer", label: "Retailer" },
  { key: "provider", label: "Provider" },
  {
    key: "avgUnit",
    label: "Avg (per kg)",
    format: (value) => formatAggregateCurrency(value),
  },
  {
    key: "lowUnit",
    label: "Low",
    format: (value) => formatAggregateCurrency(value),
  },
  {
    key: "highUnit",
    label: "High",
    format: (value) => formatAggregateCurrency(value),
  },
  { key: "n", label: "Count" },
];

const legacyStatisticCardContainerSx = {
  fontWeight: 500,
  fontSize: "16px",
  background: "#fff",
  border: "1px dashed #000",
  color: "#000",
  boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.13)",
};

const designStatisticCardContainerSx = {
  fontWeight: 500,
  fontSize: "16px",
  background: paper,
  border: `1px solid ${stone}`,
  color: ink,
  boxShadow: "none",
};

/** Chart/table frame — DS tokens when design enabled, legacy dashed frame otherwise. */
export const statisticCardContainerSx = insightsDesignEnabled()
  ? designStatisticCardContainerSx
  : legacyStatisticCardContainerSx;

/** Alias for Insights-wide frame token naming. */
export const insightsChartFrameSx = statisticCardContainerSx;

const designInsightsChartPlotSurfaceSx = {
  backgroundColor: paper,
  border: `1px solid ${stone}`,
  borderRadius: 0,
  color: ink,
  boxShadow: "none",
};

const legacyInsightsChartPlotSurfaceSx = {
  backgroundColor: legacyStatisticCardContainerSx.background,
  border: legacyStatisticCardContainerSx.border,
  borderRadius: 0,
  color: legacyStatisticCardContainerSx.color,
  boxShadow: legacyStatisticCardContainerSx.boxShadow,
};

/** Paper inset for charts inside accordion bodies. */
export const insightsChartPlotSurfaceSx = insightsDesignEnabled()
  ? designInsightsChartPlotSurfaceSx
  : legacyInsightsChartPlotSurfaceSx;

/** Matches Pricing charts / ChartBlock section headings. */
export const pricingReportChartTitleSx = {
  fontSize: "0.95rem",
  fontWeight: 700,
  textTransform: "uppercase",
};

function humanizeWords(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

export function formatProviderOptionLabel(providerValue) {
  if (!providerValue) return "";
  const raw = String(providerValue).trim();
  if (!raw) return "";
  const segment = raw.includes(":") ? raw.split(":").pop() : raw;
  return humanizeWords(segment.replace(/[_-]+/g, " "));
}

export function formatProductOptionLabel(product) {
  if (!product) return "";
  const displayName = humanizeWords(product.name || "");
  const rawName = humanizeWords(product.raw_product_name || "");
  return `${displayName}${rawName ? ` (${rawName})` : ""}`;
}
